import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  MuiBox,
  Button,
  Checkbox,
  Typography,
  Card,
  FlexBox,
  InputField,
  Grid,
  SelectDropdown
} from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { selectors as adminsSelectors, actions as adminsActions, CreateAdminPayload, Role } from 'store/admins';
import { useSelector } from 'react-redux';
import {
  getInitialForm,
  validationSchema,
  getHasEmptyInput,
  getHasInvalidInput,
  defaultRoles,
  globalRole
} from '../utils';
import { getSortedList } from 'utils/stringFormat';
import { MAINTENANCE, INVALID, VALID } from '../constants';
import { ValidPasswordToolTip } from '../../ValidPasswordToolTip';
import { selectors as venuesSelectors, actions as venuesActions, Venue } from 'store/venues';
import { selectors as authSelectors } from 'store/auth';
import { useStyles } from '../styled';
import { RoleItem } from '../types';

export const CreateNewUserForm = () => {
  // ACTION CREATORS AND SELECTORS
  const [createAdmin, getVenues, getValidPinCode, resetError, resetPinCode] = useActions([
    adminsActions.createAdmin,
    venuesActions.getVenues,
    adminsActions.getValidPinCode,
    adminsActions.resetError,
    adminsActions.resetPinCode
  ]);
  const { pending, error } = useSelector(adminsSelectors.selectAction);
  const venues = useSelector(venuesSelectors.selectVenues);
  const admin = useSelector(authSelectors.selectAdmin);
  const validPincode = useSelector(adminsSelectors.selectPinCode);
  const classes = useStyles();

  // SETS THE FORM VALUES AND VALIDATION VALUES FOR EACH FORM VALUE (INVALID | VALID)
  const initialForm = getInitialForm();
  const [form, setForm] = useState(initialForm);
  const [validation, setValidation] = useState<{ [x: string]: string }>({});
  const [validationMessage, setValidationMessage] = useState<{ [x: string]: string | null }>({});
  const [showPasswordInfo, setPasswordInfoVisibility] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState<Venue>(venues[0]);
  const [roles, setRoles] = useState<RoleItem[]>([]);
  const [selectedRole, setSelectedRole] = useState<RoleItem | null>(null);

  // GET venue list if its not available
  useEffect(() => {
    if (!venues || !venues.length) getVenues();
    if (!validPincode) getValidPinCode();

    return () => resetError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (venues && venues.length) setSelectedVenue(venues[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venues]);

  // ON UPDATE OF FORM.ROLE
  useEffect(() => {
    // SET THE FORM TO BE THE PREVIOUS FROM WITH PASSWORD KEY ADDED
    // Resetting password to empty if role change to Maintenance

    if (form.role === MAINTENANCE) {
      setForm((f) => ({ ...f, password: '' }));
    }
  }, [form.role]);

  // CHECKS INVALIDATIONS AND EMPTY INPUTS AND INITIAL VALUES
  const hasEmptyInput = useMemo(() => getHasEmptyInput(form), [form]);
  const hasInvalidInput = useMemo(() => getHasInvalidInput(validation), [validation]);

  React.useEffect(() => {
    let tempRoles: RoleItem[] = [];
    if (admin && admin.role === 'GlobalAdmin') {
      tempRoles = globalRole;
    }
    tempRoles = tempRoles.concat(defaultRoles);
    setRoles(tempRoles);
  }, [admin]);

  React.useEffect(() => {
    if (validPincode) {
      setForm({ ...form, pinCode: validPincode });
    }

    return () => resetPinCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validPincode]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  // FORM INPUT CHANGE
  const onInputChange = (key: keyof ReturnType<typeof getInitialForm>, e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    // IF THERE WAS VALIDATION ERROR FOR SELECTED INPUT - RESET IT TO VALID
    if (validation[key] === INVALID) {
      setValidation({ ...validation, [key]: VALID });
      setValidationMessage({ ...validationMessage, [key]: null });
    }

    // UPDATE THE FORM OBJECT WITH THE CHANGE TO CORRESPONDING KEY VALUE PAIR
    setForm({ ...form, [key]: e.target.value });

    if (key === 'password') {
      setPasswordInfoVisibility(true);
    }
  };

  // FORM INPUT BLUR
  const onInputBlur = async (
    key: keyof ReturnType<typeof getInitialForm>,
    e: React.ChangeEvent<HTMLInputElement>,
    value: string | null = null
  ) => {
    e.preventDefault();

    // TRIMS WHITE SPACE OF THE INPUT VALUES ONBLUR
    setForm({ ...form, [key]: e.target.value.trim() });

    let invalidMessage = null;

    // IF THE TRIMMED STRING VALUE HAS VALUE RUN VALIDATION AND SET INVALID MESSAGE
    if (value && value.toString().trim().length) {
      const result = await validationSchema.validate({ [key]: value && value.toString().trim() }).catch((err) => err);
      invalidMessage = result && result.message;
    }

    // SET VALIDATION TO BE VALID OR INVALID DEPENDING ON WHETHER INVALID MESSAGE EXISTS
    setValidation({ ...validation, [key]: invalidMessage ? INVALID : VALID });
    setValidationMessage({ ...validationMessage, [key]: invalidMessage });
    if (key === 'password') {
      setPasswordInfoVisibility(false);
    }
  };

  // SEND ALERTS CHECKBOX CLICK
  const onAlertCheckboxClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    // SETS FORM MAINTENANCE OR CLIENT ADMIN CHECKBOXES
    setForm({ ...form, sendAlertEmails: !form.sendAlertEmails });
  };

  const handleRoleChange = (role: RoleItem) => {
    setSelectedRole(role);
    setForm({ ...form, role: role.code });
  };

  // FORM INPUT VALIDATION
  const validate = (key: keyof ReturnType<typeof getInitialForm>) => {
    return validationMessage[key];
  };

  // FORM SUBMIT
  const onCreateUserClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    // SUBMITS THE FORM TO CREATE ADMIN
    const payload: CreateAdminPayload = {
      name: form.name,
      email: form.email,
      password: form.password,
      role: form.role as Role,
      sendAlertEmails: form.sendAlertEmails,
      venueId: form.role === MAINTENANCE && selectedVenue ? selectedVenue.id : undefined,
      pinCode: form.pinCode
    };
    createAdmin(payload);
  };

  // CHECKS ROLE
  const isMaintenance = form.role === MAINTENANCE;

  // CONDITIONS FOR DISABLING SUBMIT BUTTON
  const buttonDisabled = hasEmptyInput || hasInvalidInput;

  return (
    <FlexBox justify="center" flexOverride="1 1 0%" padding="0 0 11">
      <FlexBox direction="column" fullWidth align="center" justify="center">
        <Grid className={classes.createUserInnerContainer}>
          <Box flexOverride="none" fullWidth margin="0 0 5">
            <Typography className={classes.createNewUserHeading}>Create New User</Typography>
          </Box>

          <MuiBox className={classes.createVenueBox}>
            <Typography className={classes.createVenueLabel} sx={{ paddingTop: '16px' }}>
              Role
            </Typography>
            <SelectDropdown
              placeholder="Select a Role"
              items={getSortedList(roles, 'name')}
              selectedItem={selectedRole as RoleItem}
              onChange={(item: RoleItem) => handleRoleChange(item)}
              itemToString={(item: RoleItem) => (item ? item.name : '')}
            />
          </MuiBox>
          {/* INPUT - NAME */}
          <MuiBox className={classes.createUserInputBox}>
            <InputField
              label="Name"
              size="medium"
              fullWidth
              placeholder="New User's Name"
              value={form.name}
              onChange={(e) => onInputChange('name', e)}
              onBlur={(e) => onInputBlur('name', e, e.target.value.trim())}
              helperText={validate('name')}
              error={validation.name === INVALID}
            />
          </MuiBox>
          {/* INPUT - EMAIL */}
          <MuiBox className={classes.createUserInputBox}>
            <InputField
              label="Email Address"
              size="medium"
              fullWidth
              placeholder="New User's Email Address"
              value={form.email}
              onChange={(e) => onInputChange('email', e)}
              onBlur={(e) => onInputBlur('email', e, e.target.value.trim())}
              helperText={validate('email')}
              error={validation.email === INVALID}
            />
          </MuiBox>
          {!isMaintenance && (
            <MuiBox className={classes.createUserInputBox}>
              <InputField
                label="Admin Password"
                size="medium"
                fullWidth
                placeholder="Admin Password"
                value={form.password}
                type="password"
                onChange={(e) => onInputChange('password', e)}
                onBlur={(e) => onInputBlur('password', e, e.target.value.trim())}
                onFocus={() => setPasswordInfoVisibility(true)}
                helperText={validate('password')}
                error={validation.password === INVALID}
                endIcon={<ValidPasswordToolTip showValidPasswordInfo={showPasswordInfo} value={form.password || ''} />}
              />
            </MuiBox>
          )}
          <Grid className={classes.createNewUserPinBlock}>
            {/* INPUT - Pin code */}
            <MuiBox className={classes.userFormTextInputBox}>
              <InputField
                label="PIN Code"
                size="medium"
                placeholder="Enter 5 Digit PIN Code"
                value={form.pinCode}
                onChange={(e) => onInputChange('pinCode', e)}
                onBlur={(e) => onInputBlur('pinCode', e, e.target.value.trim())}
                helperText={validate('pinCode')}
                error={validation.pinCode === INVALID}
              />
            </MuiBox>
            {/* INPUT - Button */}
            <MuiBox className={classes.userFormButtonBox}>
              <Button
                customSize="small"
                onClick={getValidPinCode}
                isLoading={pending}
                width={220}
                type="submit"
                className={classes.createPinCode}
              >
                Generate New PIN
              </Button>
            </MuiBox>
          </Grid>
          {isMaintenance && (
            <MuiBox className={classes.createVenueBox}>
              <Typography className={classes.createVenueLabel}>Venue Name</Typography>
              <SelectDropdown
                placeholder="Select a Venue"
                items={getSortedList(venues, 'name')}
                selectedItem={selectedVenue}
                onChange={(item: Venue) => setSelectedVenue(item)}
                itemToString={(item: Venue) => (item ? item.name : '')}
              />
            </MuiBox>
          )}

          <Box flexOverride="none" fullWidth margin="0 1 4" right={130}>
            <Card>
              <Card.ActionArea onClick={onAlertCheckboxClick}>
                <Grid className={classes.createSendAlertBlock}>
                  <Checkbox
                    checked={form.sendAlertEmails}
                    label={<Typography>Send alert emails</Typography>}
                    size="medium"
                  />
                </Grid>
              </Card.ActionArea>
            </Card>
          </Box>
        </Grid>

        {error && <Typography className={classes.createUserErrorText}>{error}</Typography>}

        {/* FORM SUBMIT */}
        <MuiBox className={classes.createUserButtonBox}>
          <Button
            disabled={buttonDisabled}
            customSize="medium"
            onClick={onCreateUserClick}
            isLoading={pending}
            width={250}
            type="submit"
            className={classes.createUserButton}
          >
            Create User
          </Button>
        </MuiBox>
      </FlexBox>
    </FlexBox>
  );
};
